import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Search, TrendingUp, Globe, Merge, Swords, Lightbulb, Target, BarChart3, Shield, Wrench } from 'lucide-react';

const audits = [
  { key: 'specialisation', icon: Target },
  { key: 'croissance', icon: TrendingUp },
  { key: 'fusion', icon: Merge },
  { key: 'alliance', icon: Swords },
  { key: 'concurrentielles', icon: BarChart3 },
  { key: 'innovation', icon: Lightbulb },
  { key: 'international', icon: Globe },
  { key: 'audit360', icon: Search },
];

export const AuditsSection = () => {
  const { t, language } = useLanguage();

  const texts = {
    fr: {
      badge: 'Audits & Expertises',
      title: 'Nos Audits et Expertises',
      subtitle: 'Une vision stratégique complète pour accélérer votre croissance',
      audits: {
        specialisation: { title: 'Stratégies Corporate', sub: 'Spécialisation & Diversification' },
        croissance: { title: 'Stratégies Corporate', sub: 'Croissance interne' },
        fusion: { title: 'Stratégies Corporate', sub: 'Fusion / Acquisition' },
        alliance: { title: 'Stratégies Corporate', sub: 'Alliance stratégique' },
        concurrentielles: { title: 'Stratégies Business', sub: 'Concurrentielles' },
        innovation: { title: 'Stratégies', sub: "D'innovation" },
        international: { title: 'Stratégie à l\'international', sub: 'Internationalisation' },
        audit360: { title: 'Audit Stratégie', sub: '360°' },
      },
      expertise: 'Expertise',
      expertiseAudit: 'Audits et Conseils',
      expertiseManagement: 'Management de transition',
      marches: 'Marchés',
      marchesFrance: 'France (TPE, PME, Grandes entreprises)',
      marchesInternational: 'Europe & Afrique',
      pragmatique: 'Approche pragmatique',
      pragmatiqueDesc: 'Solutions sur-mesure adaptées à votre contexte',
      cta: 'Demander un audit',
      ctaSub: 'Consultation gratuite de 30 min',
      callFree: 'Appel 30 min offert',
    },
    en: {
      badge: 'Audits & Expertise',
      title: 'Our Audits and Expertise',
      subtitle: 'A comprehensive strategic vision to accelerate your growth',
      audits: {
        specialisation: { title: 'Corporate Strategies', sub: 'Specialization & Diversification' },
        croissance: { title: 'Corporate Strategies', sub: 'Internal Growth' },
        fusion: { title: 'Corporate Strategies', sub: 'Mergers & Acquisitions' },
        alliance: { title: 'Corporate Strategies', sub: 'Strategic Alliance' },
        concurrentielles: { title: 'Business Strategies', sub: 'Competitive' },
        innovation: { title: 'Innovation', sub: 'Strategies' },
        international: { title: 'International Strategy', sub: 'Internationalization' },
        audit360: { title: 'Strategic Audit', sub: '360°' },
      },
      expertise: 'Expertise',
      expertiseAudit: 'Audits & Advisory',
      expertiseManagement: 'Transition Management',
      marches: 'Markets',
      marchesFrance: 'France (SMEs, Mid-caps, Large companies)',
      marchesInternational: 'Europe & Africa',
      pragmatique: 'Pragmatic Approach',
      pragmatiqueDesc: 'Tailor-made solutions adapted to your context',
      cta: 'Request an audit',
      ctaSub: 'Free 30 min consultation',
      callFree: 'Free 30 min call',
    }
  };

  const tx = texts[language];

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="audits" className="section-padding bg-white relative overflow-hidden" data-testid="audits-section">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(#004d40 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#004d40]/5 text-[#004d40] px-4 py-2 rounded-full text-sm font-semibold mb-5 tracking-wide uppercase">
            <Search className="w-4 h-4" />
            {tx.badge}
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#004d40] mb-4" data-testid="audits-title">
            {tx.title}
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto">{tx.subtitle}</p>
        </div>

        {/* Audits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {audits.map((audit, i) => {
            const Icon = audit.icon;
            const data = tx.audits[audit.key];
            return (
              <div
                key={audit.key}
                className="group relative bg-gradient-to-br from-[#f8faf9] to-white rounded-xl p-5 border border-slate-100 hover:border-[#4caf50] hover:shadow-lg hover:shadow-[#004d40]/5 transition-all duration-300 cursor-pointer"
                onClick={scrollToContact}
                data-testid={`audit-card-${audit.key}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#004d40] flex items-center justify-center flex-shrink-0 group-hover:bg-[#4caf50] transition-colors duration-300">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#4caf50] uppercase tracking-wide">{data.title}</p>
                    <p className="text-sm font-bold text-[#004d40] mt-0.5 group-hover:text-[#00695c] transition-colors">{data.sub}</p>
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ArrowRight className="w-4 h-4 text-[#4caf50]" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom: Expertise | Marchés | CTA */}
        <div className="bg-[#004d40] rounded-2xl p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {/* Expertise */}
            <div>
              <h3 className="text-[#4caf50] font-bold text-sm uppercase tracking-wider mb-4">{tx.expertise}</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#4caf50] flex-shrink-0" />
                  <span className="text-white font-medium text-sm">{tx.expertiseAudit}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Wrench className="w-5 h-5 text-white/60 flex-shrink-0" />
                  <span className="text-white font-medium text-sm">{tx.expertiseManagement}</span>
                </li>
              </ul>
            </div>

            {/* Marchés */}
            <div>
              <h3 className="text-[#4caf50] font-bold text-sm uppercase tracking-wider mb-4">{tx.marches}</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-white/60 flex-shrink-0" />
                  <span className="text-white font-medium text-sm">{tx.marchesFrance}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-white/60 flex-shrink-0" />
                  <span className="text-white font-medium text-sm">{tx.marchesInternational}</span>
                </li>
              </ul>
              <p className="text-white/50 text-xs mt-3">{tx.pragmatiqueDesc}</p>
            </div>

            {/* CTA */}
            <div className="text-center md:text-right flex flex-col items-center md:items-end gap-3">
              <button
                onClick={scrollToContact}
                className="bg-[#4caf50] hover:bg-[#388e3c] text-white px-8 py-4 rounded-full font-bold text-base inline-flex items-center gap-2 group transition-colors duration-300"
                data-testid="audit-cta"
              >
                {tx.cta}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <p className="text-white/50 text-xs">{tx.ctaSub}</p>
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); scrollToContact(); }}
                className="inline-flex items-center gap-2 text-[#4caf50] hover:text-white text-sm font-medium transition-colors mt-1"
                data-testid="audit-call-link"
              >
                <span className="w-2 h-2 rounded-full bg-[#4caf50] animate-pulse" />
                {tx.callFree}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
